import {useLocation, useParams} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';
// navigation helpers are no longer needed here; link props used in UI components
// import {ownerPath, videoPath} from 'routes';
import useAuthRedirect from './useAuthRedirect';
import {useVideoContextQuery, useActorEmotionMutation} from './apiHooks';
import {usePlayback} from 'contexts/WatchContext';
import {TitleSubjectMetadataMessage} from '@/types/TitleSubjectMetadata';
import {VideoSubjectMetadataMessage} from '@/types/VideoSubjectMetadata';
import {ClassSubjectMetadataMessage} from '@/types/ClassSubjectMetadata';

/**
 * Controller hook for VideoContext screen: data loading, queue, and player logic.
 */
export interface VideoContextController {
  isLoading: boolean;
  error?: Error;
  watchVideo: VideoSubjectMetadataMessage;
  watchTitle: TitleSubjectMetadataMessage;
  coverList: VideoSubjectMetadataMessage[];
  matchList: VideoSubjectMetadataMessage[];
  musicPanel: number;
  setMusicPanel: (panel: number) => void;
  facadeQueue: VideoSubjectMetadataMessage[];
  onPlayNextVideo: () => void;
  onPlayPrevVideo: () => void;
  onVideoFeedback: (v: VideoSubjectMetadataMessage, heartVideo: boolean) => void;
  resetQueue: () => void;
  /** Title class categories for this title */
  classMetadata: ClassSubjectMetadataMessage[];
}

export function useVideoContextController(): VideoContextController {
  const location = useLocation();
  // Extract 'musicVideo' from route params as initial video identifier
  interface VideoContextParams {
    /** allow index signature for useParams mapping */
    [key: string]: string | undefined;
    musicVideo?: string;
  }
  const { musicVideo: musicVideoParam = '' } = useParams<VideoContextParams>();
  const [musicVideo, setMusicVideo] = useState<string>(musicVideoParam);
  // Update state when route param changes
  useEffect(() => { if (musicVideoParam) setMusicVideo(musicVideoParam); }, [musicVideoParam]);

  // Queue and player contexts
  const { facadeQueue, play, setQueue, currentVideo, navigatePrev, navigateNext } = usePlayback();

  // Tab state: 0 = from-title, 1 = like-title
  // Initialize UI panel from navigation state
  const { musicPanel: initPanel = 0 } = (location.state as any) || {};
  const [musicPanel, setMusicPanel] = useState<number>(initPanel);
  // Reset UI tab to content (0) on every new video navigation
  useEffect(() => {
    setMusicPanel(0);
  }, [musicVideoParam]);
  // If navigation state changes (new video via navigate), update UI panel
  useEffect(() => {
    const nav = (location.state as any) || {};
    if (typeof nav.musicPanel === 'number') {
      setMusicPanel(nav.musicPanel);
    }
  }, [location.state]);

  // Fetch data
  // Query for video context, including refetch for post-mutation updates
  // Local version index to key queries and force refetches, avoiding stale race conditions
  const [version, setVersion] = useState<number>(0);
  const { data, error, isLoading } = useVideoContextQuery(musicVideo, version);
  // Heart mutation hook - use mutateAsync for promise-based ordering
  const { mutateAsync: sendEmotion } = useActorEmotionMutation();
  // Redirect on auth error
  useAuthRedirect(error);

  // Seed or update queue and playback on navigation (runs on URL or state change)
  useEffect(() => {
    // Ensure we are on a video context route with fetched metadata
    if (!musicVideoParam) return;
    const watch = data?.watchMetadata;
    if (!watch) return;
    // Navigation override queues
    const nav = (location.state as any) || {};
    const overrideFacade = nav.facadeQueue as VideoSubjectMetadataMessage[] | undefined;
    const overrideShadow = nav.shadowQueue as VideoSubjectMetadataMessage[] | undefined;
    const defaultList = data.matchMetadata?.videoMetadata ?? [];
    if (overrideFacade) {
      if (currentVideo?.musicVideo === watch.musicVideo) {
        // Same video: only update the queue
        setQueue(overrideShadow ?? overrideFacade);
      } else {
        // New navigation: play with override queue
        play(watch, overrideFacade, overrideShadow);
      }
    } else {
      // No override: always play (new video)
      play(watch, defaultList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicVideoParam, data, location.key]);

  // Playback context next/prev for navigation
  const onPlayNextVideo = () => navigateNext();
  const onPlayPrevVideo = () => navigatePrev();

  // Feedback: heart/un-heart then bump version to refetch context
  const onVideoFeedback = async (v: VideoSubjectMetadataMessage, heartVideo: boolean) => {
    await sendEmotion({ musicVideo: v.musicVideo, heartVideo });
    setVersion((prev) => prev + 1);
  };

  // Destructure fetched data
  const watchVideo = data?.watchMetadata as VideoSubjectMetadataMessage;
  const watchTitle = data?.titleMetadata as TitleSubjectMetadataMessage;

  // Output lists
  const coverList = useMemo(
    () => data?.coverMetadata?.videoMetadata || [],
    [data]
  );
  const matchList = useMemo(
    () => data?.matchMetadata?.videoMetadata || [],
    [data]
  );
  // Class categories for this title
  const classMetadata = useMemo(
    () => data?.classMetadata?.classMetadata || [],
    [data]
  );

  // Reset queue to current panel list and restart playback at current video
  const resetQueue = () => {
    const list = (musicPanel === 0 && coverList.length > 1) ? coverList : matchList;
    // Use player.play to update both queue and playbackQueue in PlaybackContext
    play(watchVideo, [...list]);
  };

  // Return controller API
  return {
    isLoading,
    error: error as any,
    watchVideo,
    watchTitle,
    coverList,
    matchList,
    musicPanel,
    setMusicPanel,
    facadeQueue,
    onPlayNextVideo,
    onPlayPrevVideo,
    onVideoFeedback,
    resetQueue,
    classMetadata,
  };
}